import { DuplicateReason, PotentialDuplicateGroup } from "../types/client";

// Canonical display order for the matched criteria.
const REASON_ORDER: DuplicateReason[] = ["email", "phone", "name"];

/**
 * A raw potential-duplicate group as it may arrive from `get_clients` during the
 * backend rollout: either the new collapsed shape (`reasons[]` + `match_count`)
 * or the legacy one-group-per-criterion shape (singular `reason`).
 */
interface RawDuplicateGroup {
  client_ids?: string[];
  reasons?: DuplicateReason[];
  reason?: DuplicateReason;
  match_count?: number;
}

/**
 * Collapses potential-duplicate groups so each unique SET of client records
 * appears once, listing every criterion that set shares (canonical order) with
 * a `match_count` for severity coloring. Groups with fewer than two clients (or
 * no recognized criteria) are dropped, and results are sorted most-matches
 * first.
 *
 * Idempotent against the already-collapsed backend payload, and also collapses
 * the legacy one-group-per-criterion payload — so the duplicates UI shows each
 * record set once regardless of which `get_clients` version is deployed.
 */
export function collapsePotentialDuplicates(
  rawGroups: RawDuplicateGroup[] | undefined | null,
): PotentialDuplicateGroup[] {
  const bySet = new Map<
    string,
    { client_ids: string[]; reasons: Set<DuplicateReason> }
  >();

  for (const group of rawGroups ?? []) {
    const ids = [...(group.client_ids ?? [])].sort();
    if (ids.length < 2) continue;

    const key = ids.join("|");
    const entry = bySet.get(key) ?? {
      client_ids: ids,
      reasons: new Set<DuplicateReason>(),
    };
    const groupReasons = group.reasons?.length
      ? group.reasons
      : group.reason
        ? [group.reason]
        : [];
    groupReasons.forEach((reason) => entry.reasons.add(reason));
    bySet.set(key, entry);
  }

  return Array.from(bySet.values())
    .map(({ client_ids, reasons }) => {
      const ordered = REASON_ORDER.filter((reason) => reasons.has(reason));
      return { client_ids, reasons: ordered, match_count: ordered.length };
    })
    .filter((group) => group.match_count > 0)
    .sort((a, b) => b.match_count - a.match_count);
}

/**
 * True when `clientId` appears in any potential-duplicate group — i.e. the
 * backend flagged this client as a possible duplicate of another record.
 * Undefined-safe so callers can pass raw context state / an optional id.
 */
export function isClientPotentialDuplicate(
  groups: PotentialDuplicateGroup[] | undefined | null,
  clientId: string | undefined | null,
): boolean {
  if (!clientId || !groups?.length) return false;
  return groups.some((group) => group.client_ids.includes(clientId));
}
